import useFeatureFlags from '@hooks/useFeatureFlags';
import useLocalStorage, { STORAGE_KEY } from '@hooks/useLocalStorage';
import pathnames from '@src/app/routes/pathnames';
import log from '@src/datadog';
import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory } from 'react-router-dom';

interface AuthenticationContextProps {
  doLogout?: () => void;
}

const AuthenticationContext = createContext<AuthenticationContextProps>({});

const AuthenticationContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    removeUser,
    revokeTokens,
    signinRedirect,
    signoutRedirect,
    clearStaleState,
    user,
  } = useAuth();
  const history = useHistory();
  const { features } = useFeatureFlags();

  const navigateLogout = () => {
    history.push(pathnames.login());
  };

  const { removeStorage } = useLocalStorage();

  const doLogout = useCallback(async () => {
    localStorage.setItem('LOGOUT_INTENT', 'true');
    removeStorage(STORAGE_KEY.MARKETING_ID());
    await removeUser();
    await revokeTokens(['access_token', 'refresh_token']);
    await clearStaleState();

    if (features.isKeycloakLoginWithSso()) {
      navigateLogout();
    } else if (!features.isKeycloakLoginWithSso()) {
      if (user?.id_token) {
        await signoutRedirect({
          post_logout_redirect_uri: window.location.origin,
          id_token_hint: user.id_token,
        });
      } else {
        await signinRedirect();
      }
    }

    log.clearDDUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeUser, revokeTokens, clearStaleState, removeStorage]);

  const authenticationContextValue = useMemo(
    () => ({
      doLogout,
    }),
    [doLogout],
  );

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {features.isKeycloakLoginWithSso()
        ? !isLoading && isAuthenticated && children
        : !isLoading && children}
    </AuthenticationContext.Provider>
  );
};

const useAuthenticationContext = () => {
  return useContext(AuthenticationContext);
};

export {
  AuthenticationContext,
  AuthenticationContextProvider,
  useAuthenticationContext,
};
